// src/context/EvaluationContext.jsx
import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import api from "../api/axios";

export const EvaluationContext = createContext({
  user: null,           // текущий авторизованный пользователь
  pending: null,        // весь payload: pending + event + participants
  loading: false,       // идёт ли загрузка
  refresh: () => {},    // ручной рефреш из модалки
  hasBlocking: false,   // есть ли блокирующая задача
  open: false,          // хотим ли показывать модалку
  setOpen: () => {},    // управление модалкой с фронта
});

export const getPendingKey = (payload) => {
  if (!payload?.has_pending || !payload?.pending) return null;

  const pending = payload.pending;
  return [
    pending.event_id ?? payload?.event?.id ?? "none",
    pending.require_social ? "social" : "no-social",
    pending.require_skill ? "skill" : "no-skill",
    pending.require_event_rating ? "event" : "no-event",
    pending.social_done ? "social-done" : "social-open",
    pending.skill_done ? "skill-done" : "skill-open",
    pending.event_done ? "event-done" : "event-open",
    pending.people_stage || payload?.ui?.people_flow?.stage || "people-default",
    payload?.ui?.people_flow?.decision_kind || "decision-none",
    payload?.ui?.people_flow?.optional_remaining ?? "optional-none",
  ].join(":");
};

export const EvaluationProvider = ({ user, children }) => {
  const userId = user?.id || user?.user_id || null;
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const dismissedPendingKeyRef = useRef(null);
  const fetchPromiseRef = useRef(null);
  const lastRequestStartedAtRef = useRef(0);
  const backgroundTimerRef = useRef(null);

  const fetchPending = useCallback(({ silent = false } = {}) => {
    // Гостей вообще не трогаем
    if (!userId) {
      setPending(null);
      setOpen(false);
      dismissedPendingKeyRef.current = null;
      return Promise.resolve();
    }
    if (fetchPromiseRef.current) return fetchPromiseRef.current;

    lastRequestStartedAtRef.current = Date.now();
    const requestPromise = (async () => {
      if (!silent) setLoading(true);
      try {
        // ✅ реальный эндпоинт pending_next
        const res = await api.get("/ratings/pending/", {
          meta: { authOptional: false, suppressGlobalError: true },
        });

        const data = res.data || {};

        if (!data.has_pending) {
          // нет задач — ничего не показываем
          setPending(null);
          setOpen(false);
          dismissedPendingKeyRef.current = null;
          return;
        }

        // data.pending содержит PendingEvaluation, плюс есть event и participants
        setPending(data);

        const nextPendingKey = getPendingKey(data);

        // Автоматически открываем только новые pending-задачи.
        if (nextPendingKey && dismissedPendingKeyRef.current !== nextPendingKey) {
          setOpen(true);
        }
      } catch (err) {
        console.error("Failed to load pending evaluations:", err);
        setPending(null);
        setOpen(false);
      } finally {
        if (!silent) setLoading(false);
      }
    })();
    const trackedPromise = requestPromise.finally(() => {
      if (fetchPromiseRef.current === trackedPromise) {
        fetchPromiseRef.current = null;
      }
    });
    fetchPromiseRef.current = trackedPromise;
    return trackedPromise;
  }, [userId]);

  // Загружаем pending сразу после логина / при смене user
  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  useEffect(() => {
    if (!userId) return undefined;

    const refreshSilently = () => {
      if (open) return;
      void fetchPending({ silent: true });
    };
    const scheduleBackgroundRefresh = () => {
      if (open) return;
      window.clearTimeout(backgroundTimerRef.current);
      const cooldownRemaining = Math.max(
        0,
        1_500 - (Date.now() - lastRequestStartedAtRef.current)
      );
      backgroundTimerRef.current = window.setTimeout(
        refreshSilently,
        Math.max(400, cooldownRemaining)
      );
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        scheduleBackgroundRefresh();
      }
    };

    window.addEventListener("focus", scheduleBackgroundRefresh);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("vg-evaluation-refresh", refreshSilently);

    const timer = window.setInterval(() => {
      refreshSilently();
    }, 60 * 1000);

    return () => {
      window.clearTimeout(backgroundTimerRef.current);
      window.removeEventListener("focus", scheduleBackgroundRefresh);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("vg-evaluation-refresh", refreshSilently);
      window.clearInterval(timer);
    };
  }, [userId, fetchPending, open]);

  const handleSetOpen = useCallback((nextOpen) => {
    setOpen((prevOpen) => {
      const resolvedNextOpen =
        typeof nextOpen === "function" ? nextOpen(prevOpen) : Boolean(nextOpen);
      const currentPendingKey = getPendingKey(pending);

      if (!resolvedNextOpen && currentPendingKey) {
        dismissedPendingKeyRef.current = currentPendingKey;
      }
      if (resolvedNextOpen && currentPendingKey) {
        dismissedPendingKeyRef.current = null;
      }

      return resolvedNextOpen;
    });
  }, [pending]);

  // есть ли блокирующая задача
  const hasBlocking =
    Boolean(pending?.pending && pending.pending.is_blocking !== false);

  const value = useMemo(() => ({
    user,
    pending,       // { has_pending, pending, event, participants }
    loading,
    refresh: fetchPending,
    hasBlocking,
    open,
    setOpen: handleSetOpen,
  }), [fetchPending, handleSetOpen, hasBlocking, loading, open, pending, user]);

  return (
    <EvaluationContext.Provider value={value}>
      {children}
    </EvaluationContext.Provider>
  );
};
